import {memo, useEffect, useLayoutEffect, useRef, useState} from 'react';
import type {ReactNode} from 'react';
import styles from './Collapse.module.scss';

interface CollapseProps {
    in: boolean;
    children: ReactNode;
    /** Remove the content from the DOM once closed (MUI `unmountOnExit`). Leave off for SEO content: it then stays in
     *  the server HTML, hidden with `visibility: hidden` and height 0. */
    unmountOnExit?: boolean;
}

type Status = 'entered' | 'entering' | 'exiting' | 'exited';

// useLayoutEffect warns during SSR; the effect body only matters on the client.
const useIsoLayoutEffect = typeof globalThis.document === 'undefined' ? useEffect : useLayoutEffect;

// theme.transitions.getAutoHeightDuration — what MUI uses for timeout="auto".
const getAutoHeightDuration = (height: number): number => {
    if (!height) return 0;
    const constant = height / 36;
    return Math.round((4 + 15 * constant ** 0.25 + constant / 5) * 10);
};

/**
 * Plain stand-in for MUI <Collapse in timeout="auto"> (vertical, collapsedSize 0): same root / wrapper / wrapperInner
 * boxes and height transition. A panel open on first render is mounted `entered`, so it is in the server HTML.
 */
const Collapse = ({in: inProp, children, unmountOnExit = false}: CollapseProps) => {
    const [status, setStatus] = useState<Status>(inProp ? 'entered' : 'exited');
    const rootRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const firstRun = useRef(true);

    useIsoLayoutEffect(() => {
        if (firstRun.current) {
            firstRun.current = false;
            return undefined;
        }
        const root = rootRef.current;
        const wrapper = wrapperRef.current;
        if (!root || !wrapper) return undefined;

        const size = wrapper.clientHeight;
        const duration = getAutoHeightDuration(size);
        root.style.transitionDuration = `${duration}ms`;

        let timer: ReturnType<typeof setTimeout>;
        if (inProp) {
            root.style.height = '0px';
            // Force a reflow so the transition starts from 0.
            root.getBoundingClientRect();
            root.style.height = `${size}px`;
            setStatus('entering');
            timer = setTimeout(() => {
                root.style.height = '';
                setStatus('entered');
            }, duration);
        } else {
            root.style.height = `${size}px`;
            root.getBoundingClientRect();
            root.style.height = '0px';
            setStatus('exiting');
            timer = setTimeout(() => setStatus('exited'), duration);
        }

        return () => clearTimeout(timer);
    }, [inProp]);

    const closed = status === 'exited' && !inProp;
    if (closed && unmountOnExit) return null;

    let className = styles.root;
    if (status === 'entered') className += ` ${styles.entered}`;
    else if (closed) className += ` ${styles.hidden}`;

    return (
        <div ref={rootRef} className={className}>
            <div ref={wrapperRef} className={styles.wrapper}>
                <div className={styles.wrapperInner}>{children}</div>
            </div>
        </div>
    );
};

export default memo(Collapse);
